import React, {useEffect, useState} from "react";
import { Tab } from "@headlessui/react";
import {FaMapMarkerAlt, FaCalendarAlt, FaClock, FaUser, FaMinus, FaPlus, FaPaw, FaSuitcaseRolling, FaTrash} from "react-icons/fa";
import {sendEmail} from "./SendEmail"
import {useWindowDimensions} from "../contexts/WindowDimensionsContext";
import AirportAutocompleteField from "./AirportAutocompleteField";
import QuotationTexts from "../assets/jsons/Quotation.json";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

function usePassengerCount(initial = 1, min = 1, max = 9) {
    const [count, setCount] = useState(initial);
    const increment = () => setCount((c) => (c < max ? c + 1 : c));
    const decrement = () => setCount((c) => (c > min ? c - 1 : c));
    const reset = (value = 0) => setCount(value);
    return { count, increment, decrement, reset };
}

function useOptions(initialOptions = {}) {
    const [options, setOptions] = useState(initialOptions);
    const toggleOption = (key) => setOptions((opts) => ({ ...opts, [key]: !opts[key] }));
    return { options, toggleOption };
}

export function InputField({ icon: Icon, label, type = "text", id, ...rest }) {
    return (
        <label htmlFor={id} className="flex flex-col text-gray-700 text-sm font-medium">
            <span className="flex items-center mb-1">
                {Icon && <Icon className="mr-2 text-gray-400" />}
                {label}
            </span>
            <input
                id={id}
                type={type}
                className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1A2A41] focus:border-transparent transition"
                {...rest}
            />
        </label>
    );
}


function PassengerCounter({ count, label, increment, decrement }) {
    return (
        <div>
            <span className="flex mb-1 items-center text-gray-700 text-sm font-medium">
                <FaUser className="mr-2 text-gray-400" /> {label || "Passagers"}
            </span>
            <div className="flex items-center gap-10 justify-center border border-gray-300 rounded-md px-3 py-1.5 select-none">
                <button
                    onClick={decrement}
                    aria-label="Réduire le nombre de passagers"
                    className="text-gray-600 hover:text-[#1A2A41] disabled:opacity-40"
                    disabled={count <= 1}
                    type="button"
                >
                    <FaMinus />
                </button>
                <span className="font-semibold text-gray-700 text-lg">{count}</span>
                <button
                    onClick={increment}
                    aria-label="Augmenter le nombre de passagers"
                    className="text-gray-600 hover:text-[#1A2A41]"
                    type="button"
                >
                    <FaPlus />
                </button>
            </div>
        </div>
    );
}

function OptionsCheckbox({ label, checked, onChange, icon: Icon }) {
    return (
        <label className="inline-flex items-center gap-2 cursor-pointer select-none text-gray-700 font-medium text-sm">
            <input
                type="checkbox"
                className="form-checkbox h-5 w-5 text-[#1A2A41] rounded focus:ring-[#1A2A41]"
                checked={checked}
                onChange={onChange}
            />
            <Icon className="text-[#1A2A41]" /> {label}
        </label>
    );
}

function Options({ options, language }) {
    const optionItems = [
        { key: "animaux", label: QuotationTexts.availableLanguages[language].options[0], icon: FaPaw },
        { key: "bagages", label: QuotationTexts.availableLanguages[language].options[1], icon: FaSuitcaseRolling },
    ];

    return (
        <fieldset className="flex flex-col md:flex-row gap-6 md:items-center mt-2 text-gray-700 font-semibold text-sm">
            <legend className="w-full mb-2">{QuotationTexts.availableLanguages[language].optionsTitle}</legend>
            {optionItems.map(({ key, label, icon }) => (
                <OptionsCheckbox
                    key={key}
                    label={label}
                    checked={options.options[key] || false}
                    onChange={() => options.toggleOption(key)}
                    icon={icon}
                />
            ))}
        </fieldset>
    );
}

function SubmitButton({ label }) {
    return (
        <button
            type="submit"
            className="mt-6 w-full sm:w-auto bg-myDarkBlue hover:border-[#C1A45F] border-myDarkBlue border-4 text-white font-semibold rounded-md px-16 py-2.5 transition"
        >
            {label || QuotationTexts.availableLanguages["fr"].trip.buttons.quote}
        </button>
    );
}

function OneWayForm({ passengers, options, formRef, setFormHeight, windowWidth, language, setLanguage }) {
    const [step, setStep] = useState(0);
    const t = QuotationTexts.availableLanguages[language];

    const [form, setForm] = useState({
        from: "",
        to: "",
        date: "2025-07-01",
        time: "09:00",
        clientName: "",
        clientFirstName: "",
        clientEmail: "",
        clientPhone: "",
        options: {
            option1: false,
            option2: true,
        }
    });

    const handleChange = (e) => {
        setForm({ ...form, [e.target.id]: e.target.value });
    };

    const nextStep = () => {
        if (step === 0) {
            if (!form.from || !form.to) {
                alert(t.form.errors.emptyRoute);
                return;
            }
        }
        if (step < 1)
            setStep(step + 1);
    };

    const prevStep = () => {
        if (step > 0) setStep(step - 1);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.clientName || !form.clientEmail) {
            alert(t.form.errors.emptyContact);
            return;
        }
        sendEmail({
            type: "Aller simple",
            passengers,
            options,
            formData: form,
        });
    };

    useEffect(() => {
        if (formRef.current) {
            const height = formRef.current.offsetHeight;
            setFormHeight(height);
        }
    }, [formRef, windowWidth]);

    return (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-1">
            {step === 0 && (
                <div className="p-4 shadow-sm bg-gray-50 border border-gray-200 rounded-xl">
                    <p className="text-myDarkBlue uppercase whitespace-nowrap text-sm font-semibold tracking-wider">
                        {t.trip.outbound.title}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 pt-4">
                        <AirportAutocompleteField
                            icon={FaMapMarkerAlt}
                            id="from"
                            label={t.trip.outbound.departure}
                            placeholder={t.form.cityOrAirport}
                            value={form.from}
                            onChange={handleChange}
                            lang={language}
                        />
                        <AirportAutocompleteField
                            icon={FaMapMarkerAlt}
                            id="to"
                            label={t.trip.outbound.destination}
                            placeholder={t.form.cityOrAirport}
                            value={form.to}
                            onChange={handleChange}
                            lang={language}
                        />
                        <InputField
                            icon={FaCalendarAlt}
                            id="date"
                            label={t.trip.outbound.date}
                            type="date"
                            value={form.date}
                            onChange={handleChange}
                            required
                        />
                        <InputField
                            icon={FaClock}
                            id="time"
                            label={t.trip.outbound.time}
                            type="time"
                            value={form.time}
                            onChange={handleChange}
                            required
                        />
                        <PassengerCounter {...passengers} label={t.trip.outbound.passengers}/>
                    </div>
                </div>
            )}

            {step === 1 && (
                <div className="p-4 shadow-sm bg-gray-50 border border-gray-200 rounded-xl">
                    <p className="text-myDarkBlue uppercase whitespace-nowrap text-sm font-semibold tracking-wider mb-4">
                        {t.form.contactTitle}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
                        <InputField id="clientName" label={t.form.lastName} placeholder={t.form.lastName} value={form.clientName} onChange={handleChange} required />
                        <InputField id="clientFirstName" label={t.form.firstName} placeholder={t.form.firstName} value={form.clientFirstName} onChange={handleChange} required />
                        <InputField id="clientEmail" type="email" label={t.form.email} placeholder={t.form.email} value={form.clientEmail} onChange={handleChange} required/>
                        <InputField id="clientPhone" type="tel" label={t.form.phone} placeholder={t.form.phone} value={form.clientPhone} onChange={handleChange}/>
                    </div>
                </div>
            )}

            <div className="flex justify-between items-center w-full">
                {step > 0 && (
                    <button
                        type="button"
                        onClick={prevStep}
                        className="mt-6 w-full sm:w-auto bg-myDarkBlue hover:border-[#C1A45F] border-myDarkBlue border-4 text-white font-semibold rounded-md px-16 py-2.5 transition ml-auto mr-4"
                    >
                        {t.trip.buttons.previous}
                    </button>
                )}

                {step === 0 ? (
                    <div className="flex justify-between w-full flex-col md:flex-row">
                        <Options options={options} language={language} title={t.optionsTitle} />
                        <button
                            type="button"
                            onClick={nextStep}
                            className="mt-6 w-full sm:w-auto bg-myDarkBlue hover:border-[#C1A45F] border-myDarkBlue border-4 text-white font-semibold rounded-md px-16 py-2.5 transition"
                        >
                            {t.trip.buttons.next}
                        </button>
                    </div>
                ) : (
                    <SubmitButton label={t.trip.buttons.quote} />
                )}
            </div>
        </form>
    );
}

function RoundTripForm({ passengers, options, formRef, setFormHeight, windowWidth, language, setLanguage }) {
    const [allerPassengersCount, setAllerPassengersCount] = useState(1);
    const [retourPassengersCount, setRetourPassengersCount] = useState(1);
    const [retourModified, setRetourModified] = useState(false);
    const [step, setStep] = useState(0);
    const t = QuotationTexts.availableLanguages[language];
    const [form, setForm] = useState({
        allerFrom: "",
        allerTo: "",
        allerDate: "2025-07-01",
        allerTime: "09:00",
        retourDate: "2025-07-07",
        retourTime: "18:00",
        clientName: "",
        clientFirstName: "",
        clientEmail: "",
        clientPhone: "",
    });

    const handleChange = (e) => {
        setForm((prev) => ({ ...prev, [e.target.id]: e.target.value }));
    };

    const nextStep = () => {
        if (step === 0) {
            if (!form.allerFrom || !form.allerTo) {
                alert(t.form.errors.emptyRoute);
                return;
            }
        }
        if (step < 1) setStep(step + 1);
    };

    const prevStep = () => {
        if (step > 0) setStep(step - 1);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.clientName || !form.clientEmail) {
            alert(t.form.errors.emptyContact);
            return;
        }
        sendEmail({
            type: "Aller-retour",
            passengers: {
                allerPassengersCount,
                retourPassengersCount
            },
            options,
            formData: form,
        });
    };

    const handleAllerPassengersChange = (newCount) => {
        setAllerPassengersCount(newCount);
        if (!retourModified) {
            setRetourPassengersCount(newCount);
        }
    };

    const handleRetourPassengersChange = (newCount) => {
        setRetourPassengersCount(newCount);
        setRetourModified(true);
    };


    useEffect(() => {
        if (formRef.current) {
            const height = formRef.current.offsetHeight;
            setFormHeight(height);
        }
    }, [form, step, formRef, windowWidth]);

    return (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-1">
            {step === 0 && (
                <>
                    <div className="p-4 shadow-sm bg-gray-50 border border-gray-200 rounded-xl mb-3">
                        <p className="text-myDarkBlue uppercase whitespace-nowrap text-sm font-semibold tracking-wider">{t.trip.outbound.title}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 pt-4">
                            <AirportAutocompleteField
                                icon={FaMapMarkerAlt}
                                id="allerFrom"
                                label={t.trip.outbound.departure}
                                placeholder={t.form.cityOrAirport}
                                value={form.allerFrom}
                                onChange={handleChange}
                                lang={language}
                            />
                            <AirportAutocompleteField
                                icon={FaMapMarkerAlt}
                                id="allerTo"
                                label={t.trip.outbound.destination}
                                placeholder={t.form.cityOrAirport}
                                value={form.allerTo}
                                onChange={handleChange}
                                lang={language}
                            />
                            <InputField icon={FaCalendarAlt} id="allerDate" label={t.trip.outbound.date} type="date" value={form.allerDate} onChange={handleChange} required />
                            <InputField icon={FaClock} id="allerTime" label={t.trip.outbound.time} type={"time"} value={form.allerTime} onChange={handleChange} required />
                            <PassengerCounter
                                count={allerPassengersCount}
                                label={t.trip.outbound.passengers}
                                increment={() => handleAllerPassengersChange(allerPassengersCount + 1)}
                                decrement={() => handleAllerPassengersChange(allerPassengersCount - 1)}
                            />
                        </div>
                    </div>

                    <div className="p-4 shadow-sm bg-gray-50 border border-gray-200 rounded-xl">
                        <p className="text-myDarkBlue uppercase whitespace-nowrap text-sm font-semibold tracking-wider">{t.trip.return.title}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
                            <InputField
                                icon={FaCalendarAlt}
                                id="retourDate"
                                label={t.trip.return.date}
                                type="date"
                                value={form.retourDate}
                                onChange={handleChange}
                                required
                            />
                            <InputField
                                icon={FaClock}
                                id="retourTime"
                                label={t.trip.return.time}
                                type="time"
                                value={form.retourTime}
                                onChange={handleChange}
                                required
                            />
                            <PassengerCounter
                                count={retourPassengersCount}
                                label={t.trip.return.passengers}
                                increment={() => handleRetourPassengersChange(retourPassengersCount + 1)}
                                decrement={() => handleRetourPassengersChange(retourPassengersCount - 1)}
                            />
                        </div>
                    </div>
                </>
            )}

            {step === 1 && (
                <div className="p-4 shadow-sm bg-gray-50 border border-gray-200 rounded-xl">
                    <p className="text-myDarkBlue uppercase whitespace-nowrap text-sm font-semibold tracking-wider mb-4">
                        {t.form.contactTitle}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
                        <InputField id="clientName" label={t.form.firstName} placeholder={t.form.firstName} value={form.clientName} onChange={handleChange} required />
                        <InputField id="clientFirstName" label={t.form.lastName} placeholder={t.form.lastName} value={form.clientFirstName} onChange={handleChange} required />
                        <InputField id="clientEmail" type="email" label={t.form.email} placeholder={t.form.email} value={form.clientEmail} onChange={handleChange} required />
                        <InputField id="clientPhone" type="tel" label={t.form.phone} placeholder={t.form.phone} value={form.clientPhone} onChange={handleChange} />
                    </div>
                </div>
            )}

            <div className="flex justify-between items-center w-full">
                {step > 0 && (
                    <button
                        type="button"
                        onClick={prevStep}
                        className="mt-6 w-full sm:w-auto bg-myDarkBlue hover:border-[#C1A45F] border-myDarkBlue border-4 text-white font-semibold rounded-md px-16 py-2.5 transition ml-auto mr-4"
                    >
                        {t.trip.buttons.previous}
                    </button>
                )}

                {step === 0 ? (
                    <div className={"flex justify-between w-full flex-col md:flex-row"}>
                        <Options options={options} language={language}/>
                        <button
                            type="button"
                            onClick={nextStep}
                            className="mt-6 w-full sm:w-auto bg-myDarkBlue hover:border-[#C1A45F] border-myDarkBlue border-4 text-white font-semibold rounded-md px-16 py-2.5 transition"
                        >
                            {t.trip.buttons.next}
                        </button>
                    </div>
                ) : (
                    <SubmitButton label={t.trip.buttons.quote} />
                )}
            </div>
        </form>
    );
}

function MultiDestForm({ passengers, options, formRef, setFormHeight, windowWidth, language, setLanguage }) {
    const [step, setStep] = useState(0);
    const [initialPassengersSynced, setInitialPassengersSynced] = useState(true);
    const t = QuotationTexts.availableLanguages[language];

    const [form, setForm] = useState({
        legs: [
            { from: "", to: "", date: "2025-07-01", time: "09:00", passengers: { count: 1 } },
        ],
        clientName: "",
        clientFirstName: "",
        clientEmail: "",
        clientPhone: "",
    });

    const handleChange = (idx, key, value) => {
        setForm((prev) => {
            const updatedLegs = [...prev.legs];
            updatedLegs[idx][key] = value;
            return { ...prev, legs: updatedLegs };
        });
    };

    const handleClientChange = (e) => {
        const { id, value } = e.target;
        setForm((prev) => ({ ...prev, [id]: value }));
    };

    const handlePassengerChange = (idx, newCount) => {
        setForm((prev) => {
            let updatedLegs;
            if (initialPassengersSynced && idx === 0) {
                updatedLegs = prev.legs.map(() => ({ passengers: { count: newCount } }));
                updatedLegs = prev.legs.map((leg) => ({ ...leg, passengers: { count: newCount } }));
            } else {
                updatedLegs = prev.legs.map((leg, i) => (i === idx ? { ...leg, passengers: { count: newCount } } : leg));
            }
            return { ...prev, legs: updatedLegs };
        });

        if (idx !== 0 && initialPassengersSynced) {
            setInitialPassengersSynced(false);
        }
    };

    const addLeg = () => {
        setForm((prev) => {
            const lastPassengersCount = prev.legs.length > 0
                ? prev.legs[prev.legs.length - 1].passengers.count
                : passengers.count; // fallback

            return {
                ...prev,
                legs: [
                    ...prev.legs,
                    { from: "", to: "", date: "", time: "", passengers: { count: lastPassengersCount } }
                ],
            };
        });
    };

    const removeLeg = (idx) => {
        if (form.legs.length <= 1) return;
        setForm((prev) => ({
            ...prev,
            legs: prev.legs.filter((_, i) => i !== idx),
        }));
    };

    const nextStep = () => {
        if (step === 0) {
            const emptyLeg = form.legs.find(leg => !leg.from || !leg.to);
            if (emptyLeg) {
                alert(t.form.errors.emptyRoute);
                return;
            }
        }
        if (step < 1) setStep(step + 1);
    };

    const prevStep = () => {
        if (step > 0) setStep(step - 1);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const { clientName, clientEmail } = form;
        if (!clientName || !clientEmail) {
            alert(t.form.errors.emptyContact);
            return;
        }
        sendEmail({
            type: "Multi-destinations",
            options,
            formData: form,
        });
    };

    useEffect(() => {
        if (formRef.current) {
            const height = formRef.current.offsetHeight;
            setFormHeight(height);
        }
    }, [formRef, form.legs.length, windowWidth]);

    return (
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-1">
            {step === 0 && (
                <div className="space-y-3">
                    {form.legs.map((leg, idx) => (
                        <div key={idx} className="relative gap-6 p-4 shadow-sm bg-gray-50 border border-gray-200 rounded-xl">
                            <p className="text-myDarkBlue uppercase text-sm font-semibold tracking-wider">{t.trip.step.title} {idx + 1}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 pt-4">
                                <AirportAutocompleteField
                                    icon={FaMapMarkerAlt}
                                    id={`from-${idx}`}
                                    label={t.trip.outbound.departure}
                                    placeholder={t.form.cityOrAirport}
                                    value={leg.from}
                                    onChange={(e) => handleChange(idx, "from", e.target.value)}
                                    lang={language}
                                />
                                <AirportAutocompleteField
                                    icon={FaMapMarkerAlt}
                                    id={`to-${idx}`}
                                    label={t.trip.outbound.destination}
                                    placeholder={t.form.cityOrAirport}
                                    value={leg.to}
                                    onChange={(e) => handleChange(idx, "to", e.target.value)}
                                    lang={language}
                                />
                                {/*<InputField icon={FaMapMarkerAlt} id={`from-${idx}`} label="Départ" placeholder="Ville ou aéroport" value={leg.from} onChange={(e) => handleChange(idx, "from", e.target.value)} />*/}
                                {/*<InputField icon={FaMapMarkerAlt} id={`to-${idx}`} label="Destination" placeholder="Ville ou aéroport" value={leg.to} onChange={(e) => handleChange(idx, "to", e.target.value)} />*/}
                                <InputField icon={FaCalendarAlt} id={`date-${idx}`} label={t.trip.outbound.date} type="date" value={leg.date} onChange={(e) => handleChange(idx, "date", e.target.value)} />
                                <InputField icon={FaClock} id={`time-${idx}`} label={t.trip.outbound.time} type="time" value={leg.time} onChange={(e) => handleChange(idx, "time", e.target.value)} />
                                <PassengerCounter
                                    count={leg.passengers?.count || 1}
                                    label={t.trip.outbound.passengers}
                                    increment={() => {
                                        const current = form.legs[idx].passengers.count || 1;
                                        if (current < 9) {
                                            handlePassengerChange(idx, current + 1);
                                        }
                                    }}
                                    decrement={() => {
                                        const current = form.legs[idx].passengers.count || 1;
                                        if (current > 1) {
                                            handlePassengerChange(idx, current - 1);
                                        }
                                    }}
                                />

                            </div>
                            {form.legs.length > 1 && (
                                <button type="button" onClick={() => removeLeg(idx)} className="absolute top-3 right-3 text-sm text-red-500 hover:underline">
                                    <FaTrash className="inline mr-1 text-xs" /> {t.trip.step.remove}
                                </button>
                            )}
                        </div>
                    ))}

                    {form.legs.length < 9 && (
                        <button
                            type="button"
                            onClick={addLeg}
                            className="inline-flex items-center gap-2 text-sm text-blue-600 font-medium hover:underline"
                        >
                            <FaPlus className="text-xs" /> {t.trip.step.add}
                        </button>
                    )}
                </div>
            )}

            {step === 1 && (
                <div className="p-4 shadow-sm bg-gray-50 border border-gray-200 rounded-xl">
                    <p className="text-myDarkBlue uppercase text-sm font-semibold tracking-wider mb-4">{t.form.contactTitle}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
                        <InputField id="clientName" label={t.form.firstName} placeholder={t.form.firstName} value={form.ClientFirstName} onChange={handleClientChange} required />
                        <InputField id="clientFirstName" label={t.form.lastName} placeholder={t.form.lastName} value={form.ClientName} onChange={handleClientChange} required />
                        <InputField id="clientEmail" type="email" label={t.form.email} placeholder={t.form.email} value={form.ClientEmail} onChange={handleClientChange} required />
                        <InputField id="clientPhone" type="tel" label={t.form.phone} placeholder={t.form.phone} value={form.ClientPhone} onChange={handleClientChange} />
                    </div>
                </div>
            )}

            <div className="flex justify-between items-center w-full">
                {step > 0 && (
                    <button
                        type="button"
                        onClick={prevStep}
                        className="mt-6 w-full sm:w-auto bg-myDarkBlue hover:border-[#C1A45F] border-myDarkBlue border-4 text-white font-semibold rounded-md px-16 py-2.5 transition ml-auto mr-4"
                    >
                        {t.trip.buttons.previous}
                    </button>
                )}
                {step === 0 ? (
                    <div className={"flex justify-between w-full flex-col md:flex-row"}>
                        <Options options={options} language={language}/>
                        <button
                            type="button"
                            onClick={nextStep}
                            className="mt-6 w-full sm:w-auto bg-myDarkBlue hover:border-[#C1A45F] border-myDarkBlue border-4 text-white font-semibold rounded-md px-16 py-2.5 transition"
                        >
                            {t.trip.buttons.next}
                        </button>
                    </div>
                ) : (
                    <SubmitButton label={t.trip.buttons.quote} />
                )}
            </div>
        </form>
    );
}

export default function QuoteTabs({selectedIndex, setSelectedIndex, formRef, setFormHeight, language, setLanguage}) {
    const passengers = usePassengerCount(1, 1, 9);
    const options = useOptions({ animaux: false, bagages: false});
    const windowDimensions = useWindowDimensions();

    const TABS = [
        QuotationTexts.availableLanguages[language].tabs[0],
        QuotationTexts.availableLanguages[language].tabs[1],
        QuotationTexts.availableLanguages[language].tabs[2]
    ];

    return (
        <div className="max-w-[1580px] mx-auto px-6 pt-3 pb-4 bg-white rounded-lg shadow-xl">
            <Tab.Group selectedIndex={selectedIndex} onChange={(index) => setSelectedIndex(index)}>
                <Tab.List className="flex space-x-6 border-b border-gray-200 mb-5 overflow-x-auto">
                    {TABS.map((tab) => (
                        <Tab
                            key={tab}
                            className={({ selected }) =>
                                classNames(
                                    "whitespace-nowrap py-2 text-sm font-semibold uppercase tracking-wider transition outline-none",
                                    selected
                                        ? "border-b-4 border-[#C1A45F] text-[#1A2A41]"
                                        : "text-gray-400 hover:text-gray-600"
                                )
                            }
                        >
                            {tab}
                        </Tab>
                    ))}
                </Tab.List>
                <Tab.Panels>
                    <Tab.Panel><OneWayForm passengers={passengers} options={options} formRef={formRef} setFormHeight={setFormHeight} windowWidth={windowDimensions.windowWidth} language={language} setLanguage={setLanguage}/></Tab.Panel>
                    <Tab.Panel><RoundTripForm passengers={passengers} options={options} formRef={formRef} setFormHeight={setFormHeight} windowWidth={windowDimensions.windowWidth} language={language} setLanguage={setLanguage}/></Tab.Panel>
                    <Tab.Panel><MultiDestForm passengers={passengers} options={options} formRef={formRef} setFormHeight={setFormHeight} windowWidth={windowDimensions.windowWidth} language={language} setLanguage={setLanguage}/></Tab.Panel>
                </Tab.Panels>
            </Tab.Group>
        </div>
    );
}

